import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import * as A from './displayActions';
import type { Deferred } from './displayTypes';
import type { DisplayItem, DisplayOrientation, ScheduleSlot } from '../../../components/display/data/types';

export function useDisplayStore() {
  const dispatch = useAppDispatch();
  const { items, loading, saving, error, loadedLocationId, editingItemId, editingMediaId } = useAppSelector((s) => s.display);

  const fetchDisplays = useCallback(
    (locationId: string, search?: string) => dispatch(A.displayFetchRequest({ locationId, search })),
    [dispatch],
  );

  const saveDisplay = useCallback(
    (params: {
      id?: string;
      locationId: string;
      displayName: string;
      mode: 'S' | 'P';
      mediaId?: string;
      playlistId?: string;
      fallbackMediaId: string;
      orientation: DisplayOrientation;
      timeSlots: ScheduleSlot[];
    }): Promise<DisplayItem> => {
      return new Promise<DisplayItem>((resolve, reject) => {
        const deferred: Deferred<DisplayItem> = { resolve, reject };
        dispatch(A.displaySaveRequest({ ...params, deferred }));
      });
    },
    [dispatch],
  );

  const deleteDisplay = useCallback(
    (id: string, locationId: string): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        const deferred: Deferred = { resolve, reject };
        dispatch(A.displayDeleteRequest({ id, locationId, deferred }));
      });
    },
    [dispatch],
  );

  const setEditingItemId = useCallback((id: string | null, mediaId: string | null = null) => dispatch(A.displaySetEditingId(id, mediaId)), [dispatch]);

  return useMemo(() => ({
    items,
    loading,
    saving,
    error,
    loadedLocationId,
    editingItemId,
    editingMediaId,
    fetchDisplays,
    saveDisplay,
    deleteDisplay,
    setEditingItemId,
  }), [items, loading, saving, error, loadedLocationId, editingItemId, editingMediaId, fetchDisplays, saveDisplay, deleteDisplay, setEditingItemId]);
}

export function useDisplayToastStore() {
  const dispatch = useAppDispatch();
  const { toasts } = useAppSelector((s) => s.display);

  const addToast = useCallback(
    (message: string, type: 'success' | 'error' | 'info' = 'success') => {
      const id = `dt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      dispatch(A.displayToastAdd({ id, message, type }));
      setTimeout(() => {
        dispatch(A.displayToastRemove(id));
      }, 3000);
    },
    [dispatch],
  );

  const removeToast = useCallback((id: string) => dispatch(A.displayToastRemove(id)), [dispatch]);

  return useMemo(() => ({ toasts, addToast, removeToast }), [toasts, addToast, removeToast]);
}
